/**
 * Owns one question: should we ask this person what they want to see?
 *
 * Deliberately the same shape as useDiscoverTour, and gated on the same kind
 * of server-side flag, because it is the same kind of decision. The flag lives
 * on the account (`discovery_prefs_set_at`) rather than in localStorage for
 * exactly the reason the tour's does: browser storage would re-ask on a second
 * device and again after any cleared browser, so a user of three months could
 * be asked to set up their feed as though they had just arrived.
 *
 * The two first-run surfaces are ordered rather than concurrent — preferences
 * first, then MAHI's walkthrough — since the tour points at a card stack whose
 * contents this question decides. DiscoveryFeed enforces that by feeding
 * `prefsPending` into the tour's own `ready` gate.
 */
import { useCallback, useEffect, useState } from 'react';
import { useMahiAuth } from '../../auth';
import { resetDiscoverFeed } from '../../prefetch';

export default function useDiscoveryPrefs({ user, authLoading, ready }) {
  const { refresh } = useMahiAuth();
  const [active, setActive] = useState(false);
  // Latches once, so a `refresh()` landing while the modal is open — or right
  // after it closes, before the new flag has propagated — cannot reopen it.
  const [settled, setSettled] = useState(false);

  useEffect(() => {
    if (settled || authLoading || !ready || !user) return;
    if (user.discovery_prefs_set_at) { setSettled(true); return; }
    setActive(true);
    setSettled(true);
  }, [settled, authLoading, ready, user]);

  /**
   * The modal is done with.
   *
   * `answered` is false only when the picker could not load its own options.
   * Nothing was saved, so nothing is written and no feed is refetched — but
   * `settled` deliberately stays true, closing the question for this session
   * only. Un-latching it would re-run the effect that opened the modal, which
   * would reopen it against the same failing request, forever. The server flag
   * is still absent, so the next session asks again, which is the right place
   * for the retry: recording "no preference" for a question we failed to
   * actually ask would be the worse outcome.
   */
  const finish = useCallback(({ answered = true } = {}) => {
    setActive(false);
    if (!answered) return;
    // The cached decks were built for the old (absent) preferences, so drop
    // them before refreshing — otherwise the feed keeps serving the promise
    // those picks resolved and the answer looks like it did nothing.
    resetDiscoverFeed();
    // Refresh so the auth context carries the new flag and the resolved
    // categories. `settled` alone only guards this mount: navigate to the
    // wardrobe and back and the feed remounts against a still-stale `user`.
    refresh();
  }, [refresh]);

  // `prefsSettled` is what the tour waits on: the two first-run surfaces are
  // ordered, not concurrent, and "settled" is the only signal that covers both
  // ways this one can end — answered, or given up on for the session.
  return { prefsActive: active, prefsSettled: settled, finishPrefs: finish };
}
